import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import {
  Box,
  Button, MobileStepper, Step,
  StepLabel,
  Stepper
} from "@mui/material";
import { limit, orderBy, where } from "firebase/firestore";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { BrowserView, MobileView } from "react-device-detect";
import { useAuthContext } from "../../Auth";
import { useSettingsContext } from "../../SettingsContext";
import { ProgressContainer } from "../../components/UI/ProgressContainer";
import { DBUser, ReservationStatus, RoomReservation, UserRole } from "../../ostertalertypes";
import { createReservations, fetchAllocationBookings } from "../../utils/database";
import { daysBetween, timestampNoHours } from "../../utils/dateutils";
import { handleError } from "../../utils/utils";
import { StepHouse } from "./StepHouse";
import { StepMultipleRooms } from "./StepMultipleRooms";
import { StepRestriction } from "./StepRestriction";
import { StepSelectUser } from "./StepSelectUser";
import { StepSummary } from "./StepSummary";
import { StepperProvider, useStepperContext } from "./StepperContext";

/**
 * Some small wrapper component. Without this the BookingStepper itself would
 * not receive a rerender by the provider.
 * @param props
 * @returns
 */
export const PageBookingStepper: React.FC<IPageBookingStepper> = (props) => {
  return (
    <StepperProvider>
      <PageBookingStepperInternal showUserSelection={props.showUserSelection} />
    </StepperProvider>)
}

interface IPageBookingStepper {
  showUserSelection?: boolean
}

export const PageBookingStepperInternal: React.FC<IPageBookingStepper> = (props) => {
  let headlines = ["Haus auswählen", "Zimmer auswählen", "Zusammenfassung"];
  const authCtx = useAuthContext();
  const stepperCtx = useStepperContext();
  const settingsCtx = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [bookingRequestSend, setBookingRequestSend] = useState(false);
  const [bookingDistance, setBookingDistance] = useState(-2);


  const resetStepper = () => {
    setBookingRequestSend(false);
    stepperCtx.resetActiveBooking();
  }




  useEffect(() => {
    const fetchRoomReservations = async () => {
      const roomReservations = await fetchAllocationBookings(
        false,
        where("status", "in", [ReservationStatus.BOOKING_REQUESTED, ReservationStatus.ACCEPTED,
          ReservationStatus.CANCEL_REQUESTED, ReservationStatus.CANCEL_REJECTED]), // all states which are blocking the calender and count as valid.
        where("userid", "==", authCtx.userDetails?.userid),
        orderBy('createdDate', 'desc'),
        limit(1)
      );
      if (roomReservations.length === 1) {
        const distance = daysBetween(new Date(), timestampNoHours(roomReservations[0].createdDate!).toMillis());
        setBookingDistance(distance);
      } else {
        setBookingDistance(-1);
      }
    };

    fetchRoomReservations();
  }, [authCtx.userDetails?.userid, bookingDistance]);

  const isRestricted = () => {
    if (authCtx.isAdmin()) {
      return <StepHouse />
    } else {
      let bookingAllowed = true;
      if (bookingDistance === -1) {
        // no booking done until now => bookings allowed
        bookingAllowed = true;
      }
      else if (bookingDistance === -2) { // still on default. prevent neither of the two options
        return <></>
      }
      else if (settingsCtx.settings.reservation.bookingRestrictionDays === 0) {
        // restriction is turned off
        bookingAllowed = true;
      } else {
        if (bookingDistance >= settingsCtx.settings.reservation.bookingRestrictionDays) {
          bookingAllowed = true;
        } else {
          bookingAllowed = false;
        }
      }

      if (authCtx.userDetails && bookingAllowed) {
        return <StepHouse />
      } else {
        const blockedForDays = settingsCtx.settings.reservation.bookingRestrictionDays - bookingDistance;
        return <StepRestriction blockedDays={blockedForDays} />
      }
    }
  }

  const steps: React.ReactElement[] = [
    isRestricted(),
    <StepMultipleRooms />,
    <StepSummary bookingRequestSend={bookingRequestSend} />
  ];


  if (props.showUserSelection) {
    steps.unshift(<StepSelectUser />);
    headlines.unshift("Gast auswählen");
  }

  const isAnonmousUser = () => {
    return stepperCtx.selectedUser && stepperCtx.selectedUser.userid?.length === 0;
  }


  const bookIt = async () => {

    if (stepperCtx.activeBooking[0].guestCount === 0) {
      enqueueSnackbar("Bitte Anzahl Gäste auswählen", {
        variant: "error",
        autoHideDuration: 6000,
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      return;
    }

    try {
      setIsLoading(true);
      // user himself is booking
      let bookingUser: DBUser = authCtx.userDetails!;

      const newAnonymous = !!stepperCtx.selectedUser && isAnonmousUser();
      // take selected user
      if (!newAnonymous && stepperCtx.selectedUser?.userid && stepperCtx.selectedUser.userid?.length > 0) {
        bookingUser = stepperCtx.selectedUser;
      }

      // availability is checked on the server in a transaction. For a new anonymous guest the account is
      // created by the server as well, and only if the booking succeeds.
      const result = await createReservations(
        stepperCtx.activeBooking,
        authCtx.isAdmin() && stepperCtx.selectedUser !== undefined && !newAnonymous ? bookingUser.userid : undefined,
        newAnonymous ? { firstName: stepperCtx.selectedUser!.firstname, lastName: stepperCtx.selectedUser!.lastname } : undefined);
      // bookingCount is updated by the server
      if (newAnonymous) {
        const selected = stepperCtx.selectedUser!;
        authCtx.addAnonymAccountToCache(result.userid, selected.firstname, selected.lastname);
        stepperCtx.setSelectedUser({ ...selected, userid: result.userid, bookingCount: 0, role: UserRole.ANONYM });
      }
      if (stepperCtx.selectedUser) {
        stepperCtx.selectedUser.bookingCount = stepperCtx.selectedUser.bookingCount + 1;
      } else if (authCtx.userDetails) {
        authCtx.userDetails.bookingCount = authCtx.userDetails.bookingCount + 1;
      }
      setIsLoading(false);
      let msg = "Reservierungsanfrage erfolgreich abgeschickt";
      if (authCtx.isAdmin()) {
        if (settingsCtx.settings?.reservation.emailOnReservationUpdate && stepperCtx.selectedUser?.role !== UserRole.ANONYM) {
          msg = "Buchung eingetragen und Bestätigungsmail an Mitglied verschickt";
        }
        else {
          msg = "Buchung eingetragen";
        }
      }

      enqueueSnackbar(msg, {
        variant: "success",
        persist: false,
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      setBookingRequestSend(true);
    } catch (error) {
      handleError("Reservierung nicht erfolgreich", error, enqueueSnackbar, setIsLoading)
    }
  };

  const handleNext = async () => {
    let activePage = steps[stepperCtx.activeStep];
    if (activePage.type === StepSummary) {
      await bookIt();
    } else if (stepperCtx.selectedHouse && activePage.type === StepHouse) {
      stepperCtx.updateSelectedHouse(stepperCtx.selectedHouse);
      stepperCtx.setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
    else if (activePage.type === StepSelectUser && isAnonmousUser() && stepperCtx.selectedUser) {
      if (authCtx.isUserExisting(stepperCtx.selectedUser?.firstname, stepperCtx.selectedUser?.lastname)) {
        stepperCtx.setDialogOpen(true);
      } else {
        stepperCtx.setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    } else {
      stepperCtx.setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    stepperCtx.setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setBookingRequestSend(false);
  };

  const areBookingsValid = (): boolean => {
    const reducer = (valid: boolean, app: RoomReservation) => {
      if (app.roomName === "" || app.from === undefined || app.to === undefined || app.guestCount <= 0) {
        return false;
      }
      return true;
    };
    return stepperCtx.activeBooking.reduce(reducer, true);
  };

  const isNextBtnDisabled = (): boolean => {
    const activePage = steps[stepperCtx.activeStep];
    if (!authCtx.isAdmin() && bookingDistance === -2) { // disable next button until it was fetched if user is allowed to book. admin is always allowed.
      return true;
    }
    if (activePage.type === StepRestriction) {
      return true;
    }
    else if (activePage.type === StepMultipleRooms) {
      return !areBookingsValid();
    } else if (activePage.type === StepSelectUser) {
      if (stepperCtx.userType === UserRole.NORMAL_ACCOUNT && !stepperCtx.selectedUser) { // no user selected in autocomplete box
        return true;
      }
      // no user filled into text fields
      else if (stepperCtx.userType === UserRole.ANONYM && (!stepperCtx.selectedUser || stepperCtx.selectedUser?.firstname.length === 0 || stepperCtx.selectedUser?.lastname.length === 0)) {
        return true;
      }
    } else if (stepperCtx.activeStep === headlines.length - 1) {
      return bookingRequestSend || isLoading;
    }
    return false;
  };

  const getMobileBackBtn = (): React.ReactNode => {
    if (stepperCtx.activeStep === headlines.length - 1 && bookingRequestSend && authCtx.isAdmin()) {
      return <Button size="small" onClick={resetStepper}>Neue Buchung</Button>
    }
    else {
      return <Button size="small" onClick={handleBack} disabled={stepperCtx.activeStep === 0}><KeyboardArrowLeft />Zurück</Button>
    }

  }
  // TODO consider showing the material ui mobile stepper on mobiles. it's consuming less space
  // => this will avoid the horizontal scroll bar in the admin view
  return (
    <ProgressContainer isLoading={isLoading}>
      <h1 className="text-2xl mt-0 mb-10 text-gray-500 text-center md:text-left">{authCtx.isAdmin() ? "Buchung eintragen" : "Buchungsanfrage erstellen"}</h1>
      <BrowserView>
        <Stepper activeStep={stepperCtx.activeStep} className="pb-5 w-full mb-15">
          {headlines.map((label, index) => {
            const stepProps: { completed?: boolean } = {};
            const labelProps: {
              optional?: React.ReactNode;
            } = {};
            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        <div className="my-10">{steps[stepperCtx.activeStep]}</div>
        <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
          {!bookingRequestSend && (
            <Button color="inherit" disabled={stepperCtx.activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
              Zurück
            </Button>
          )}
          {bookingRequestSend && stepperCtx.activeStep === headlines.length - 1 && authCtx.isAdmin() && (
            <Button color="inherit" disabled={stepperCtx.activeStep === 0} onClick={resetStepper} sx={{ mr: 1 }}>
              Weitere Buchung
            </Button>
          )}
          <Box sx={{ flex: "1 1 auto" }} />
          <Button disabled={isNextBtnDisabled()} onClick={handleNext}>
            {stepperCtx.activeStep === headlines.length - 1 ? "Buchen" : "Weiter"}
          </Button>
        </Box>
      </BrowserView>
      <MobileView>
        <Box sx={{ maxWidth: 400, flexGrow: 1 }}>
          <h1 className="text-2xl mt-0 mb-10 text-gray-500 text-center md:text-left">{headlines[stepperCtx.activeStep]}</h1>
          <div className="my-10">{steps[stepperCtx.activeStep]}</div>
          <MobileStepper
            variant="text"
            steps={steps.length}
            position="static"
            activeStep={stepperCtx.activeStep}
            nextButton={
              <Button size="small" disabled={isNextBtnDisabled()} onClick={handleNext}>
                {stepperCtx.activeStep === headlines.length - 1 ? "Buchen" : <>Weiter <KeyboardArrowRight /></>}
              </Button>
            }
            backButton={getMobileBackBtn()}
          />
        </Box>
      </MobileView>
    </ProgressContainer>
  );
};


